import React, { useCallback, useState } from 'react';
import { Form, Formik } from 'formik';
import { VscError, VscEye, VscEyeClosed } from 'react-icons/vsc';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import Switch from 'design-system/Switch';
import { useAuth } from 'hooks/authHook';
import { useMFAContext } from 'hooks/useMFA';
import {
  requestGetAccessToken,
  requestGetUserInfos,
  requestOauth,
} from 'services/authServices';
import {
  getSigninInfo,
  getUserAndPassword,
} from 'store/actions/signin-actions';
import { handleUserId } from 'store/actions/user-actions';

import {
  Column,
  Intro,
  Title,
  Subtitle,
  FormBody,
  Fields,
  Field,
  TextInput,
  ErrorText,
  ErrorTag,
  ToggleVisibility,
  Actions,
  PrimaryButton,
  OutlineButton,
} from 'components/layout/Auth/AuthForm/styles';

import { Options, ForgotPasswordLink } from './styles';

type LoginFormProps = {
  setShowModalRestPass: React.Dispatch<React.SetStateAction<boolean>>;
};

type LoginValues = {
  username: string;
  password: string;
  rememberMe: boolean;
};

type AuthContextValue = {
  signIn: (data: {
    loginEmail: string;
    accessToken: string;
    refreshToken: string;
    customerType: string;
  }) => void;
  setUserInfos: (data: Record<string, unknown>) => void;
};

type MFAContextValue = {
  credentials: { login: string; password: string; token: string };
  setCredentials: (credentials: {
    login: string;
    password: string;
    token: string;
  }) => void;
  setUserId: (userId: string) => void;
};

type RequestError = {
  response?: {
    status?: number;
    data?: {
      code?: string;
      message?: string;
      user?: { id?: string };
    };
  };
};

/** Texto do layout para credencial inválida (o 401 genérico da API). */
const CREDENCIAL_INVALIDA = 'E-mail ou senha inválidos';

const signinSchema = Yup.object().shape({
  password: Yup.string().required('Preencha sua senha'),
  username: Yup.string()
    .email('Insira um e-mail válido')
    .required('Preencha seu e-mail'),
});

const LoginForm: React.FC<LoginFormProps> = ({ setShowModalRestPass }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const { signIn, setUserInfos } = useAuth() as AuthContextValue;
  const history = useHistory();
  const dispatch = useDispatch();

  const {
    credentials: { token: lsMFAtoken },
    setCredentials,
    setUserId,
  } = useMFAContext() as MFAContextValue;

  const handleSelectAddress = useCallback(
    (
      customerSiteCode: string,
      customerSiteId: string,
      customerCode: string,
      customerId: string,
      customerName: string,
      addressLine: string,
      addressNumber: string,
      userCustomerRelationshipId: string,
      document: string
    ) => {
      setLoading(false);
      setUserInfos({
        customerSiteCode,
        customerSiteId,
        customerCode,
        customerId,
        customerName,
        customerAddressLine: `${addressLine}, ${addressNumber}`,
        userCustomerRelationshipId,
        document,
      });
    },
    [setUserInfos]
  );

  const handleSubmit = useCallback(
    async ({ username, password }: LoginValues): Promise<boolean> => {
      setLoading(true);
      setAuthError(null);
      dispatch(getUserAndPassword(password));
      try {
        sessionStorage.setItem('@UltragazEmpresarial:X-Mfa-Login', username);
        setCredentials({ login: username, password, token: '' });

        const token = lsMFAtoken || null;

        const accessToken = await requestGetAccessToken();
        const responseOauth = await requestOauth(
          username,
          password,
          token,
          accessToken,
          dispatch,
          getSigninInfo
        );

        if (responseOauth) {
          signIn({
            loginEmail: username,
            accessToken: responseOauth.access_token,
            refreshToken: responseOauth.refresh_token,
            customerType: 'Granel',
          });
          requestGetUserInfos(
            username,
            responseOauth.access_token,
            dispatch,
            handleUserId,
            setUserInfos,
            handleSelectAddress,
            history
          );

          return true;
        }
        return false;
      } catch (error) {
        setLoading(false);
        const { response } = error as RequestError;
        const userId = response?.data?.user?.id || null;
        const code = response?.data?.code;
        const message = response?.data?.message;
        const status = response?.status;

        if (status === 401) {
          if (code === '401.004') {
            setShowModalRestPass(true);
            return false;
          }
          if (code === '401.006') {
            toast.warn(message);
            return false;
          }
          if (code === '401.007') {
            if (!userId) {
              setAuthError(CREDENCIAL_INVALIDA);
              return false;
            }
            toast.warn('Você precisa fazer uma verificação de duplo fator');
            localStorage.removeItem('@UltragazEmpresarial:X-Mfa-Hash');
            setUserId(userId);
            history.push('/validar-token');
            return false;
          }
        }
        if (status === 401) {
          setAuthError(CREDENCIAL_INVALIDA);
          return false;
        }
        if (message) {
          toast.error(message);
          return false;
        }
        toast.error('Ocorreu um erro interno na aplicação');
        return false;
      }
    },
    [
      dispatch,
      setCredentials,
      lsMFAtoken,
      signIn,
      setUserInfos,
      handleSelectAddress,
      history,
      setShowModalRestPass,
      setUserId,
      setAuthError,
    ]
  );

  const passwordToggle = (
    <ToggleVisibility
      type="button"
      onClick={() => setShowPassword((prev) => !prev)}
      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
    >
      {showPassword ? <VscEye /> : <VscEyeClosed />}
    </ToggleVisibility>
  );

  return (
    <Column>
      <Intro>
        <Title>
          Bem-vindo ao
          <br />
          Portal Minha Ultragaz
        </Title>
        <Subtitle>
          Aqui você terá acesso a um painel personalizado com todas as
          informações sobre seus abastecimentos, boletos, notas e manutenções
        </Subtitle>
      </Intro>

      <Formik
        initialValues={{
          username: localStorage.getItem('rememberedEmail') || '',
          password: '',
          rememberMe: !!localStorage.getItem('rememberedEmail'),
        }}
        validationSchema={signinSchema}
        onSubmit={(values: LoginValues) => {
          if (values.rememberMe) {
            localStorage.setItem('rememberedEmail', values.username);
          } else {
            localStorage.removeItem('rememberedEmail');
          }
          handleSubmit(values);
        }}
      >
        {({
          handleChange,
          handleBlur,
          values,
          errors,
          touched,
          isValid,
          setFieldValue,
        }) => (
          <Form autoComplete="off">
            <FormBody>
              <Fields>
                <Field>
                  <TextInput
                    id="username"
                    name="username"
                    type="email"
                    label="E-mail"
                    placeholder="Digite"
                    required
                    fullWidth
                    error={
                      (!!errors.username && !!touched.username) || !!authError
                    }
                    value={values.username}
                    onChange={(e) => {
                      setAuthError(null);
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                  />
                  {errors.username && touched.username && (
                    <ErrorText>{errors.username}</ErrorText>
                  )}
                </Field>

                <Field>
                  <TextInput
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    label="Senha"
                    placeholder="Digite"
                    required
                    fullWidth
                    error={
                      (!!errors.password && !!touched.password) || !!authError
                    }
                    value={values.password}
                    onChange={(e) => {
                      setAuthError(null);
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                    trailingIcon={passwordToggle}
                  />
                  {errors.password && touched.password && (
                    <ErrorText>{errors.password}</ErrorText>
                  )}
                </Field>

                {authError && (
                  <ErrorTag role="alert">
                    <VscError aria-hidden="true" />
                    {authError}
                  </ErrorTag>
                )}

                <Options>
                  <Switch
                    name="rememberMe"
                    label="Lembrar de mim"
                    checked={values.rememberMe}
                    onChange={(checked) => setFieldValue('rememberMe', checked)}
                  />
                  <ForgotPasswordLink href="/esqueci-senha">
                    Esqueceu sua senha?
                  </ForgotPasswordLink>
                </Options>
              </Fields>

              <Actions>
                <PrimaryButton
                  type="submit"
                  shape="pill"
                  fullWidth
                  disabled={
                    loading || !isValid || !values.username || !values.password
                  }
                >
                  {loading ? 'Entrando...' : 'Entrar'}
                </PrimaryButton>
                <OutlineButton
                  type="button"
                  variant="secondary"
                  shape="pill"
                  fullWidth
                  onClick={() => history.push('/novo-primeiro-acesso-granel')}
                >
                  Criar conta
                </OutlineButton>
              </Actions>
            </FormBody>
          </Form>
        )}
      </Formik>
    </Column>
  );
};

export default LoginForm;
